import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Select from "react-dropdown-select";
import { useQuery, useMutation, useLazyQuery } from "@apollo/react-hooks";
import { LIST_STAFF, CREATE } from "../../queries/appointment.query";
import { GET_OUT_PATIENT, GET_IN_PATIENT } from "../../queries/patient.query";
import LoadingSpinner from "../../partials/loading";
import { toast } from "react-toastify";
import { CleanMessage, AppName } from "../../util/app";
import DatePicker from "react-datepicker";
import AuthProvider from "../../providers/Auth.Provider";
import { sendNotification } from "../../sockette";
import { ACTION_TYPE, EVENT_TARGET } from "../../event/model";

function Create({ history }) {
    // Local State

    const [model, setModel] = useState({ date: new Date() });
    const [patientPage, setPatientPage] = useState(1);
    const [limit] = useState(1500);

    const [query, setQuery] = useState(0);

    const types = [
        { id: 1, name: "Admitted" },
        { id: 2, name: "Not Admitted" },
    ];

    const [patients, setPatients] = useState([]);
    const { site } = AuthProvider.authProvider.GetAuthenticatedStaff();

    // local ref

    const title = "Book Appointment";

    // Staff query
    const { loading, data } = useQuery(LIST_STAFF, {
        variables: { site: site.id },
        onError: (e) => toast.error(CleanMessage(e.message)),
    });
    // post new appointment

    const [createFunc, { loading: cLoading }] = useMutation(CREATE, {
        onCompleted: (d) => {
            const { message } = d.BookAppointment;
            toast.success(message);
            sendNotification(message, ACTION_TYPE.CREATE, EVENT_TARGET.APPOINTMENT);
            history.push("/home/clinical-note/appointment");
        },
        onError: (e) => toast.error(CleanMessage(e.message)),
    });
    // GET OUT-PATIENT

    const [getOutPatientFunc, { loading: outLoading }] = useLazyQuery(GET_OUT_PATIENT, {
        onError: (e) => toast.error(CleanMessage(e.message)),
        onCompleted: (d) => {
            setPatients(d.GetOutPatients.docs);
        },
    });

    const [getInPatientFunc, { loading: inLoading }] = useLazyQuery(GET_IN_PATIENT, {
        onError: (e) => toast.error(CleanMessage(e.message)),
        onCompleted: (d) => {
            setPatients(d.GetPatients.docs);
        },
    });

    useEffect(() => {
        if (query === 1) {
            getOutPatientFunc({ variables: { page: patientPage, limit } });
        } else if (query === 2) {
            setPatientPage(1);
            getInPatientFunc({ variables: { page: patientPage, limit } });
        }
    }, [query, getOutPatientFunc, getInPatientFunc, patientPage, limit]);

    return (
        <>
            <Helmet>
                <title>
                    {title} | {AppName}
                </title>

                <link href="../../main/formlayout.css" rel="stylesheet" type="text/css" />
            </Helmet>

            <div className="card">
                <div className="card-head">
                    <header>New Appointment</header>
                </div>
                <div className="card-body m-b-20">
                    <div>
                        <LoadingSpinner loading={loading || cLoading} />
                    </div>
                    <form
                        onSubmit={async (evt) => {
                            //
                            evt.preventDefault();
                            //
                            document.body.scrollTop = 0;
                            document.documentElement.scrollTop = 0;
                            // staff validation
                            if (model && !model.assignee) {
                                toast.warn("Please select an assignee!");
                                return;
                            }
                            // staff department
                            if (model && !model.department) {
                                toast.warn("Please select a department!");
                                return;
                            }

                            await createFunc({ variables: { model } });
                        }}
                    >
                        <div className="row">
                            <div className="col-3 bg-primary text-center">
                                <h5>Patient*</h5>
                            </div>
                            <table className="table table-bordered">
                                <tbody>
                                    <tr>
                                        <td style={{ backgroundColor: "aliceblue", width: "30%" }}>
                                            <strong>Type*</strong>
                                        </td>
                                        <td>
                                            <Select
                                                className="input-height"
                                                options={types}
                                                multi={false}
                                                valueField="id"
                                                labelField="name"
                                                onChange={(values) => setQuery(values[0].id)}
                                                searchable={true}
                                                color="#0066f5"
                                                searchBy="name"
                                                clearable={true}
                                                placeholder="- Please Select -"
                                                separator={true}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ backgroundColor: "aliceblue", width: "30%" }}>
                                            <strong>Patient*</strong>
                                        </td>
                                        <td colSpan="2">
                                            <LoadingSpinner loading={outLoading || inLoading} bar={true} />
                                            <Select
                                                className="input-height"
                                                options={patients}
                                                multi={false}
                                                valueField="id"
                                                labelField="name"
                                                onChange={(values) => setModel({ ...model, patient: values[0].id })}
                                                searchable={true}
                                                color="#0066f5"
                                                searchBy="name"
                                                clearable={true}
                                                placeholder="- Please Select -"
                                                separator={true}
                                                createNewLabel="Load More"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ backgroundColor: "aliceblue", width: "30%" }}>
                                            <strong>Date & TIme*</strong>
                                        </td>
                                        <td colSpan="2">
                                            <DatePicker
                                                selected={model.date}
                                                onChange={(date) => setModel({ ...model, date })}
                                                className="form-control input-height"
                                                timeIntervals={5}
                                                showTimeSelect
                                                timeCaption="Time"
                                                dateFormat="MMM d, yyyy h:mm aa"
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="row">
                            <div className="col-3 bg-primary text-center">
                                <h5>Assignee*</h5>
                            </div>

                            <table className="table table-bordered">
                                <tbody>
                                    <tr>
                                        <td style={{ backgroundColor: "aliceblue", width: "30%" }}>
                                            <strong>Staff*</strong>
                                        </td>
                                        <td>
                                            <Select
                                                options={
                                                    data &&
                                                    data.GetStaffList.docs.map((item) => ({
                                                        id: item.id,
                                                        display: `${item.surname} ${item.first_name} (${item.email})`,
                                                    }))
                                                }
                                                className="input-height"
                                                multi={false}
                                                valueField="id"
                                                labelField="display"
                                                onChange={(values) => setModel({ ...model, assignee: values[0].id })}
                                                searchable={true}
                                                color="#0066f5"
                                                searchBy="display"
                                                clearable={true}
                                                separator={true}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ backgroundColor: "aliceblue", width: "30%" }}>
                                            <strong>Department*</strong>
                                        </td>
                                        <td>
                                            <Select
                                                className="input-height"
                                                options={data && data.GetDepartments.docs}
                                                multi={false}
                                                valueField="id"
                                                labelField="name"
                                                onChange={(values) => setModel({ ...model, department: values[0].id })}
                                                searchable={true}
                                                color="#0066f5"
                                                searchBy="name"
                                                clearable={true}
                                                separator={true}
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="row">
                            <div className="col-3 bg-primary text-center">
                                <h5>Note (If any)</h5>
                            </div>

                            <table className="table table-bordered">
                                <tbody>
                                    <tr>
                                        <td>
                                            <textarea
                                                name="note-id"
                                                id="note-id"
                                                onChange={({ currentTarget }) => setModel({ ...model, note: currentTarget.value })}
                                                className="form-control form-control-textarea"
                                                rows="6"
                                                placeholder="enter note"
                                            ></textarea>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="form-group text-center m-t-20">
                            <button type="submit" className="mdl-button mdl-button--raised mdl-js-ripple-effect margin-right-10 m-t-20 btn-circle btn-success">
                                Book Appointment
                                <i className="material-icons">keyboard_arrow_down</i>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    history.push("/home/clinical-note/appointment");
                                }}
                                className="mdl-button mdl-button--raised mdl-js-ripple-effect m-t-20 btn-circle"
                            >
                                Cancel <i className="material-icons text-danger">close</i>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}

export default Create;
